/* eslint-disable complexity */
import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { usePearlToast } from 'framework';
import { useStore } from 'stores/Store';
import { TokenService } from 'lib/apis/TokenService';
import { WebstoreService } from 'shared/lib/apis/WebstoreService';
import { VendorAuthService } from 'lib/apis/VendorAuthService';
import { APP_ROUTES } from 'shared/constants/navigation';
import { NewWebstoreSetupStepForms } from 'vendor/constants/WebstoreSetupSteps';
import {
    hasBusinessDetails,
    redirectToVendorConnectOrFallback,
    getEmailFromVendorConnectToken,
} from 'shared/lib/VendorConnectRedirect';
import EmailVerificationPanel from '../VendorBasicDetails/EmailVerificationPanel';
import { LoginContainer } from './VendorLoginForm.styled';

const EmailVerificationPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { toastError, toastSuccess } = usePearlToast();
    const { authUser, vendor, webstore } = useStore();
    const webstoreApi = WebstoreService();
    const tokenService = TokenService();
    const vendorAuthService = VendorAuthService();

    const emailFromParams = searchParams.get('email');
    const emailFromToken = getEmailFromVendorConnectToken();
    const emailFromAuth = authUser.Email;

    // Priority: URL params > JWT token > auth user
    const emailToUse = emailFromParams || emailFromToken || emailFromAuth;

    const [otpSent, setOtpSent] = useState(false);
    const [isSendingOtp, setIsSendingOtp] = useState(false);
    const [isEmailAlreadyVerified, setIsEmailAlreadyVerified] = useState(false);
    const otpSentRef = useRef(false);
    const abortControllerRef = useRef(null);

    // Combined effect: Check email verification status AND send OTP if needed
    useEffect(() => {
        if (!emailToUse) {
            toastError('Email is required for verification');
            navigate(APP_ROUTES.vendor.auth.login);
            return;
        }

        // Check if email is already verified (check directly, not via state)
        const isAlreadyVerified = authUser?.email_verified === 1;

        if (isAlreadyVerified) {
            console.log('[EmailVerification] email-already-verified', { email: emailToUse });
            setIsEmailAlreadyVerified(true);
            otpSentRef.current = true; // Prevent OTP from being sent
            return;
        }

        // Prevent sending OTP multiple times
        if (otpSentRef.current) {
            return;
        }

        // Abort any previous request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const sendOtp = async () => {
            otpSentRef.current = true;
            setIsSendingOtp(true);

            // Create new abort controller for this request
            abortControllerRef.current = new AbortController();

            try {
                await vendorAuthService.resendEmailOTP(emailToUse);
                // Only update state if request wasn't aborted
                if (!abortControllerRef.current.signal.aborted) {
                    setIsSendingOtp(false);
                    setOtpSent(true);
                    console.log('[EmailVerification] otp-sent', { email: emailToUse });
                    toastSuccess('Verification code sent to your email');
                }
            } catch (error) {
                // istanbul ignore next
                // Ignore abort errors
                if (error.name === 'AbortError') {
                    console.log('[EmailVerification] otp-request-cancelled');
                    return;
                }
                // istanbul ignore next
                console.error('[EmailVerification] error-sending-otp', error);
                // Only update state if request wasn't aborted
                if (!abortControllerRef.current.signal.aborted) {
                    // istanbul ignore next
                    toastError('Failed to send verification code. Please try again.');
                    setIsSendingOtp(false);
                    otpSentRef.current = false;
                }
            }
        };

        sendOtp();

        // Cleanup: abort request on unmount
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [emailToUse, authUser?.email_verified]);

    const handleSignUpInstead = async () => {
        try {
            // Logout the current session if user is logged in
            if (authUser?.Id) {
                const accessToken = localStorage.getItem('accessToken');
                if (accessToken) {
                    try {
                        await vendorAuthService.logoutVendor(accessToken);
                        console.log('[EmailVerification] session-logged-out-for-signup');
                    } catch (logoutError) {
                        console.warn(
                            '[EmailVerification] logout-failed-but-continuing',
                            logoutError
                        );
                    }
                }
            }

            // Clear shared Vendor Connect cookies
            document.cookie =
                'VENDORCONNECT_PEARL_ACCESS_TOKEN=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
            document.cookie =
                'VENDORCONNECT_PEARL_REFRESH_TOKEN=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
            document.cookie =
                'VENDORCONNECT_PEARL_ID_TOKEN=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

            console.log('[EmailVerification] shared-cookies-cleared-for-signup');

            // Clear auth store
            authUser.setDefaults();

            navigate(APP_ROUTES.vendorNew);
        } catch (error) {
            console.error('[EmailVerification] error-signing-up-instead', error);
            toastError('Failed to process sign up. Please try again.');
        }
    };

    const handleEmailVerified = async () => {
        try {
            toastSuccess('Email verified successfully!');

            // Fetch user and webstore data
            const userWebstore = await webstoreApi.getWebstoreByUserId(authUser.Id);

            if (userWebstore?.id) {
                authUser.Roles.Vendor = true;
                webstore.setFromApi(userWebstore);

                const currentSetupStep = userWebstore?.CurrentSetupStep;
                console.log('[EmailVerification] vendor-data-loaded', {
                    vendorId: vendor?.id || null,
                    webstoreId: webstore?.Id || null,
                    currentSetupStep,
                    hasBusinessDetails: hasBusinessDetails(userWebstore),
                });

                // If user was in the middle of setup, continue from where they left off
                if (currentSetupStep && currentSetupStep !== 'COMPLETE') {
                    console.log(
                        '[EmailVerification] email-verified -> navigate to current setup step',
                        { step: currentSetupStep }
                    );
                    navigate(`${APP_ROUTES.vendor.webstore.newsetup}/${currentSetupStep}`);
                    return;
                }

                // Redirect based on setup completion status
                if (vendor && vendor.id) {
                    if (hasBusinessDetails(userWebstore)) {
                        console.log(
                            '[EmailVerification] email-verified -> redirectToVendorConnect'
                        );
                        redirectToVendorConnectOrFallback(tokenService, navigate);
                    } else {
                        // Start setup from business details page
                        console.log(
                            '[EmailVerification] email-verified -> navigate business details page'
                        );
                        navigate(
                            `${APP_ROUTES.vendor.webstore.newsetup}/${NewWebstoreSetupStepForms.BUSINESS}`
                        );
                    }
                } else {
                    // New vendor, go to welcome page
                    navigate(APP_ROUTES.vendor.auth.welcome);
                }
            } else {
                // No webstore, go to welcome page
                navigate(APP_ROUTES.vendor.auth.welcome);
            }
        } catch (error) {
            // istanbul ignore next
            console.error('Error during email verification redirect:', error);
            // istanbul ignore next
            toastError('Error completing email verification. Please try again.');
        }
    };

    // Show message if email is already verified
    if (isEmailAlreadyVerified) {
        return (
            <LoginContainer>
                <div
                    style={{
                        textAlign: 'center',
                        padding: '40px 20px',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                    }}
                >
                    <p
                        style={{
                            margin: 0,
                            fontSize: '16px',
                            fontWeight: 600,
                            color: '#040404',
                        }}
                    >
                        Email is already verified
                    </p>
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 64 64"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <circle cx="32" cy="32" r="32" fill="#3a6767" />
                        <path
                            d="M24 32L30 40L44 24"
                            stroke="white"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
            </LoginContainer>
        );
    }

    if (isSendingOtp) {
        return (
            <LoginContainer>
                <p>Sending verification code...</p>
            </LoginContainer>
        );
    }

    if (!otpSent) {
        return (
            <LoginContainer>
                <p>Unable to send verification code. Please try again.</p>
            </LoginContainer>
        );
    }

    return (
        <LoginContainer>
            <EmailVerificationPanel
                email={emailToUse}
                onVerified={handleEmailVerified}
                constrainCodeFieldWidth={false}
            />
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <button
                    onClick={handleSignUpInstead}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: '#315454',
                        textDecoration: 'none',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        padding: '0',
                    }}
                    onMouseEnter={(e) => (e.target.style.textDecoration = 'underline')}
                    onMouseLeave={(e) => (e.target.style.textDecoration = 'none')}
                >
                    Sign up instead
                </button>
            </div>
        </LoginContainer>
    );
};

export { EmailVerificationPage };
